import { useState } from "react";
import { useTranslation } from "react-i18next";
import { ErrorBox, Skeleton } from "../../components/ui";
import { useLabourReconciliation } from "../../lib/api";
import { SectionCard, Stat } from "./shared";

const VERDICT_CLS: Record<string, string> = {
  matched: "bg-green-50 text-status-green",
  over: "bg-amber-50 text-status-amber",
  under: "bg-blue-50 text-brand-primary",
  no_attendance: "bg-brand-grey-light text-brand-grey",
};

/** Hours booked against hours worked (HRX-02 → HRX-03).
 *
 * Until time capture existed there was nothing to check a timesheet against: a week claiming forty hours looked
 * exactly like a true one. Neither number is the truth alone — booked over worked is a week written up from
 * memory, worked over booked is time that never reached a project at all, which is how a project quietly
 * under-reports its own cost.
 *
 * The comparison runs only on days attendance actually covers, and says how many those were. Measuring a month
 * of timesheets against two days of punches would report everybody as wildly over-booked, and the finding would
 * be about the rollout rather than about them. */
export default function Reconciliation({ company }: { company: string }) {
  const { t } = useTranslation();
  const [days, setDays] = useState(30);
  const q = useLabourReconciliation(company || undefined, days);

  if (q.isError) return <div className="mt-6"><ErrorBox error={q.error} onRetry={() => void q.refetch()} /></div>;
  if (!q.data) return <div className="mt-6 grid gap-3 md:grid-cols-4">{[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-24" />)}</div>;
  const d = q.data;

  return (
    <div className="mt-4 space-y-4" data-labour-reconciliation>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="max-w-3xl text-sm text-brand-grey">
          {t("lab.recHint", { days: d.days_with_attendance, from: d.from, to: d.to })}
        </p>
        <div className="flex gap-1 text-xs">
          {[7, 30, 90].map((v) => (
            <button key={v} onClick={() => setDays(v)} data-labour-window={v}
              className={`rounded-full border px-3 py-1 font-semibold ${
                days === v ? "border-brand-primary bg-brand-primary text-white" : "border-brand-grey-light text-brand-grey"}`}>
              {t("lab.lastDays", { n: v })}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 md:grid-cols-5" data-labour-rec-strip>
        <Stat label={t("lab.compared")} value={d.summary.people} hint={t("lab.comparedHint", { days: d.days_with_attendance })} />
        <Stat label={t("lab.verdict.matched")} value={d.summary.matched} cls={d.summary.matched ? "text-status-green" : ""} />
        <Stat label={t("lab.verdict.over")} value={d.summary.over_booked} cls={d.summary.over_booked ? "text-status-amber" : ""} />
        <Stat label={t("lab.verdict.under")} value={d.summary.under_booked} cls={d.summary.under_booked ? "text-brand-primary" : ""} />
        <Stat label={t("lab.neverBook")} value={d.on_site_but_never_book} hint={t("lab.neverBookHint")} />
      </div>

      <SectionCard title={t("lab.recTitle")} aside={t("lab.recRange", { from: d.from, to: d.to })}>
        {d.rows.length === 0 ? (
          <div className="p-6 text-center text-sm text-brand-grey" data-labour-rec-empty>{t("lab.recEmpty")}</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-[#F6F8FA] text-left text-[11px] uppercase tracking-wide text-brand-grey">
              <tr>
                <th className="px-4 py-2">{t("wfa.person")}</th>
                <th className="px-2 py-2 text-right">{t("lab.daysCompared")}</th>
                <th className="px-2 py-2 text-right">{t("lab.booked")}</th>
                <th className="px-2 py-2 text-right">{t("lab.worked")}</th>
                <th className="px-2 py-2 text-right">{t("lab.gap")}</th>
                <th className="px-4 py-2">{t("ops.status")}</th>
              </tr>
            </thead>
            <tbody>
              {d.rows.map((r) => (
                <tr key={r.employee} className="border-t border-brand-grey-light" data-labour-rec-row={r.employee}>
                  <td className="px-4 py-2">
                    <div className="font-medium">{r.employee_name ?? r.employee}</div>
                    <div className="text-[11px] text-brand-grey">{r.company}</div>
                  </td>
                  <td className="px-2 py-2 text-right tabular-nums text-brand-grey">{r.days_compared}</td>
                  <td className="px-2 py-2 text-right tabular-nums">{r.booked.toFixed(2)}</td>
                  <td className="px-2 py-2 text-right tabular-nums">{r.worked.toFixed(2)}</td>
                  <td className={`px-2 py-2 text-right font-semibold tabular-nums ${r.gap > 0 ? "text-status-amber" : r.gap < 0 ? "text-brand-primary" : ""}`}>
                    {r.gap > 0 ? "+" : ""}{r.gap.toFixed(2)}
                    {r.gap_pct !== null && <span className="ml-1 text-[10px] font-normal text-brand-grey">{r.gap_pct > 0 ? "+" : ""}{r.gap_pct}%</span>}
                  </td>
                  <td className="px-4 py-2">
                    <span className={`rounded px-2 py-0.5 text-[11px] font-semibold ${VERDICT_CLS[r.verdict] ?? ""}`}>
                      {t(`lab.verdict.${r.verdict}`)}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </SectionCard>
    </div>
  );
}
